/**
 * CP1Entry — Couple Portrait entry screen.
 * data-screen-label="cp-01 Entry · invitation"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton } from '../../chrome/Button';
import { CPBackdrop, CPStars, CPAvatar } from './shared';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

interface CP1EntryProps {
  flow: UseCoupleFlowReturn;
  partnerUsername?: string;
  /** Catalog SKU pricing this portrait — drives the eyebrow price (149 vs upsell 99). */
  skuCode?: string;
}

function ErrorToast({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: '10px 14px',
        borderRadius: 8,
        background: 'rgba(200, 77, 122, 0.12)',
        border: '0.5px solid rgba(200, 77, 122, 0.45)',
        fontSize: 'var(--type-ui)',
        color: '#ffd6e0',
        lineHeight: 1.4,
      }}
    >
      {message}
    </div>
  );
}

function humanizeError(err: string, t: (k: string) => string): string {
  if (err.includes('partner_not_found')) return t('couple.entry.errorPartnerNotFound');
  if (err.includes('chart_not_ready')) return t('couple.entry.errorChartNotReady');
  return err;
}

export function CP1Entry({ flow, partnerUsername: prefill, skuCode = 'couple_portrait' }: CP1EntryProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('flows');
  const [username, setUsername] = useState(prefill ?? '');

  // Pre-fill from prop when it changes
  useEffect(() => {
    if (prefill) setUsername(prefill);
  }, [prefill]);

  const isLoading = flow.state === 'initiating';
  const canSubmit = username.trim().length > 0 && !isLoading;

  function handleInput(e: React.ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value.replace(/^@+/, '').slice(0, 32);
    setUsername(raw);
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    void flow.initiate(username.trim());
  }

  return (
    <div className={styles.root} data-screen-label="cp-01 Entry · invitation">
      <CPBackdrop tone="quiet" />
      <CPStars count={40} />

      <div className={styles.content}>
        {/* Nav */}
        <AppHeader title={t('couple.headerTitle')} onBack={() => navigate(-1)} headingId="couple-heading" />

        {/* Eyebrow */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <div className={styles.eyebrow}>{t('couple.entry.eyebrow', { stars: SKU_CATALOG[skuCode].stars })}</div>
          <h1 className={styles.displayTitleCenter}>
            {t('couple.entry.titlePrefix')} <em className={styles.gradAccent}>{t('couple.entry.titleAccent')}</em>
          </h1>
        </div>

        {/* Dual avatar preview */}
        <div className={styles.avatarPairRow}>
          <CPAvatar glyph="🌙" palette="gold" size={80} />
          <span className={styles.avatarPairSep}>+</span>
          <CPAvatar glyph="✦" palette="rose" size={80} />
        </div>

        {/* Status grid */}
        <div className={styles.statusGrid}>
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>{t('couple.entry.statusYourArchetype')}</span>
            <span className={styles.statusReady}>{t('couple.entry.statusReady')}</span>
          </div>
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>
              {flow.partnerUsername ? `@${flow.partnerUsername}` : '…'}
            </span>
            <span className={styles.statusPending}>{t('couple.entry.statusPending')}</span>
          </div>
        </div>

        {/* Username input */}
        <form onSubmit={handleSubmit}>
          <div className={styles.inputWrap}>
            <div className={styles.inputLabel}>{t('couple.entry.inputLabel')}</div>
            <div className={styles.inputRow}>
              <span className={styles.inputAt}>@</span>
              <input
                className={styles.inputField}
                type="text"
                placeholder={t('couple.entry.inputPlaceholder')}
                value={username}
                onChange={handleInput}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={32}
              />
            </div>
          </div>

          {/* Error */}
          {flow.error && <ErrorToast message={humanizeError(flow.error, t)} />}

          {/* CTA */}
          <div className={styles.ctaArea}>
            <PrimaryButton
              type="submit"
              disabled={!canSubmit}
            >
              {isLoading ? <span className={styles.spinner} /> : null}
              {t('couple.entry.sendCta')}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}
